const singletons = new WeakMap();
const factoryFns = new WeakMap();

/**
 * Returns (lazily instantiating if necessary) a singleton of a given class/function type.
 *
 * @param fnOrClass Function or class type of the singleton instance.
 * @return The singleton instance.
 */
export function getSingleton<C>(fnOrClass: (() => C) | (new () => C)): C {
  let instance = singletons.get(fnOrClass);
  if (!instance) {
    const factoryFn = factoryFns.get(fnOrClass);
    if (factoryFn) {
      instance = factoryFn();
    } else {
      instance = new (fnOrClass as new () => C)();
    }
    singletons.set(fnOrClass, instance);
  }
  return instance;
}

/**
 * Registers a factory function associated to a function/class type in case that
 * function/class does not provide a standard way of constructing itself.
 *
 * @param fnOrClass Function or class type assocaited to the factory.
 * @param factoryFn Factory function.
 */
export function registerSingletonFactory<C>(
  fnOrClass: (() => C) | (new () => C),
  factoryFn: () => C,
) {
  factoryFns.set(fnOrClass, factoryFn);
}

/**
 * Mocks singleton instance for a given class/function type.
 *
 * @param fnOrClass Function or class type of the singleton instance.
 * @param instance Singleton instance.
 * @return A disposer that when called will restore the previous singleton (if any).
 */
// eslint-disable-next-line @typescript-eslint/ban-types
export function mockSingletonInstance<C>(
  fnOrClass: (() => C) | (new () => C),
  instance: C,
) {
  const previousInstance = singletons.get(fnOrClass);

  singletons.set(fnOrClass, instance);

  return () => {
    if (!previousInstance) {
      singletons.delete(fnOrClass);
    } else {
      singletons.set(fnOrClass, previousInstance);
    }
  };
}
